import React, { useEffect, useState, useCallback } from "react";
import PageHeader from "../components/common/PageHeader";
import Button from "../components/common/Button";
import DataTable from "../components/common/Table";
import Modal, { FIELD_CLASS, LABEL_CLASS } from "../components/common/Modal";
import ConfirmModal from "../components/common/ConfirmModal";
import useStore from "../store/useStore";
import api from "../services/api";

const defaultForm = {
  assetId: "",
  assetName: "",
  location: "",
  description: "",
  maintenanceType: "Other",
  cost: "",
  date: "",
  performedBy: "",
  status: "Pending"
};

const hasCost = (value) => {
  if (value === "" || value === null || value === undefined) return false;
  const amount = Number(value);
  return Number.isFinite(amount) && amount >= 0;
};

const formatStoredDate = (value) => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return "";
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).toLocaleDateString();
};

const Maintenance = () => {
  const { toast } = useStore();

  const [maintenance, setMaintenance] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMaintenance, setSelectedMaintenance] = useState(null);
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState(defaultForm);

  const fetchMaintenance = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/maintenance");
      setMaintenance(res.data.data || []);
    } catch (error) {
      toast("Failed to load maintenance records", "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchAssets = useCallback(async () => {
    try {
      const res = await api.get("/assets");
      setAssets(res.data.data || []);
    } catch (error) {
      console.error("Error fetching assets:", error);
    }
  }, []);

  useEffect(() => {
    fetchMaintenance();
    fetchAssets();
  }, [fetchMaintenance, fetchAssets]);

  const closeForm = () => {
    setForm(defaultForm);
    setSelectedMaintenance(null);
    setFormOpen(false);
  };

  const openCreate = () => {
    setForm(defaultForm);
    setSelectedMaintenance(null);
    setFormOpen(true);
  };

  const handleAssetChange = (e) => {
    const assetId = e.target.value;

    if (!assetId) {
      setForm((prev) => ({ ...prev, assetId: "", assetName: "", location: "" }));
      return;
    }

    const asset = assets.find((a) => a.id === parseInt(assetId, 10));
    setForm((prev) => ({
      ...prev,
      assetId,
      assetName: asset?.assetName || "",
      location: asset?.location || ""
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const openEdit = (item) => {
    const asset = assets.find((a) => a.id === item.assetId);
    setSelectedMaintenance(item);
    setForm({
      assetId: item.assetId?.toString() || "",
      assetName: item.assetName || asset?.assetName || "",
      location: item.location || asset?.location || "",
      description: item.description || "",
      maintenanceType: item.maintenanceType || "Other",
      cost: item.cost === 0 || item.cost ? String(item.cost) : "",
      date: item.date ? String(item.date).slice(0, 10) : "",
      performedBy: item.performedBy || "",
      status: item.status === "Completed" ? "Completed" : "Pending"
    });
    setFormOpen(true);
  };

  const handleAdd = async () => {
    if (!form.assetId || !hasCost(form.cost) || !form.date) {
      toast("Asset, a cost of zero or more, and a date are required", "error");
      return;
    }

    try {
      setLoading(true);
      const response = await api.post("/maintenance", {
        assetId: parseInt(form.assetId, 10),
        description: form.description?.trim() || null,
        maintenanceType: form.maintenanceType,
        cost: parseFloat(form.cost),
        date: form.date,
        performedBy: form.performedBy?.trim() || null,
        status: form.status === "Completed" ? "Completed" : "Pending"
      });
      toast(response.data?.message || "Maintenance record added successfully", "success");
      closeForm();
      fetchMaintenance();
    } catch (error) {
      toast(error.response?.data?.message || "Failed to add maintenance", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedMaintenance) {
      toast("Please select a record to update", "error");
      return;
    }

    if (!form.assetId || !hasCost(form.cost) || !form.date) {
      toast("Asset, a cost of zero or more, and a date are required", "error");
      return;
    }

    try {
      setLoading(true);
      await api.put(`/maintenance/${selectedMaintenance.id}`, {
        assetId: parseInt(form.assetId, 10),
        description: form.description?.trim() || null,
        maintenanceType: form.maintenanceType,
        cost: parseFloat(form.cost),
        date: form.date,
        performedBy: form.performedBy?.trim() || null,
        status: form.status === "Completed" ? "Completed" : "Pending"
      });
      toast("Maintenance record updated successfully", "success");
      closeForm();
      fetchMaintenance();
    } catch (error) {
      toast(error.response?.data?.message || "Failed to update maintenance", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      setLoading(true);
      await api.delete(`/maintenance/${deleteTarget.id}`);
      toast("Maintenance record deleted successfully", "success");
      setDeleteTarget(null);
      fetchMaintenance();
    } catch (error) {
      toast(error.response?.data?.message || "Failed to delete maintenance", "error");
    } finally {
      setLoading(false);
    }
  };

  const filtered = maintenance.filter((item) => {
    const q = search.toLowerCase();
    return (
      (item.assetName || "").toLowerCase().includes(q) ||
      (item.description || "").toLowerCase().includes(q) ||
      (item.performedBy || "").toLowerCase().includes(q) ||
      (item.status || "").toLowerCase().includes(q)
    );
  });

  return (
    <div>
      <PageHeader
        title="Maintenance"
        action={
          <Button variant="primary" icon="add" onClick={openCreate}>
            Add Record
          </Button>
        }
      />

      <DataTable
        columns={[
          { key: "assetName", label: "Asset Name", render: (row) => row.assetName || "N/A" },
          { key: "location", label: "Location", render: (row) => row.location || "N/A" },
          { key: "description", label: "Description", render: (row) => row.description || "-" },
          {
            key: "maintenanceType",
            label: "Type",
            render: (row) => (
              <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                {row.maintenanceType || "Other"}
              </span>
            )
          },
          { key: "performedBy", label: "Performed By", render: (row) => row.performedBy || "-" },
          {
            key: "cost",
            label: "Service Cost",
            sortValue: (row) => parseFloat(row.cost || 0),
            render: (row) => `GMD ${parseFloat(row.cost || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`
          },
          {
            key: "status",
            label: "Status",
            render: (row) => (
              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${row.status === "Completed" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                {row.status === "Completed" ? "Completed" : "Pending"}
              </span>
            )
          },
          {
            key: "date",
            label: "Date",
            sortValue: (row) => new Date(row.date || row.createdAt || 0).getTime(),
            render: (row) => formatStoredDate(row.date) || (row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "N/A")
          }
        ]}
        rows={filtered}
        loading={loading && !formOpen && !deleteTarget}
        search={search}
        onSearchChange={(e) => setSearch(e.target.value)}
        searchPlaceholder="Search by asset, description or technician..."
        onEdit={openEdit}
        onDelete={setDeleteTarget}
        emptyMessage="No maintenance records found."
      />

      <Modal
        open={formOpen}
        title={selectedMaintenance ? "Update Maintenance" : "Add Maintenance"}
        onClose={closeForm}
        footer={
          <>
            <Button variant="secondary" onClick={closeForm} disabled={loading}>
              Cancel
            </Button>
            {selectedMaintenance ? (
              <Button variant="success" icon="update" onClick={handleUpdate} disabled={loading}>
                Update
              </Button>
            ) : (
              <Button variant="primary" icon="add" onClick={handleAdd} disabled={loading}>
                Add
              </Button>
            )}
          </>
        }
      >
        <div className="grid md:grid-cols-2 gap-x-12 gap-y-6">
          <div className="space-y-6">
            <div>
              <label className={LABEL_CLASS}>
                Asset Name <span className="text-red-500">*</span>
              </label>
              <select
                name="assetId"
                value={form.assetId}
                onChange={handleAssetChange}
                className={FIELD_CLASS}
              >
                <option value=""></option>
                {assets.map((asset) => (
                  <option key={asset.id} value={asset.id}>
                    {asset.assetName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={LABEL_CLASS}>Location</label>
              <input type="text" value={form.location} disabled className={FIELD_CLASS} />
            </div>
            <div>
              <label className={LABEL_CLASS}>Description</label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                rows={4}
                placeholder="Describe the maintenance work..."
                className={`${FIELD_CLASS} resize-none`}
              />
            </div>
          </div>
          <div className="space-y-6">
            <div>
              <label className={LABEL_CLASS}>Maintenance Type</label>
              <select name="maintenanceType" value={form.maintenanceType} onChange={handleChange} className={FIELD_CLASS}>
                <option value="Cleaning">Cleaning</option>
                <option value="Repairs">Repairs</option>
                <option value="Painting">Painting</option>
                <option value="Electrical">Electrical</option>
                <option value="Plumbing">Plumbing</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className={LABEL_CLASS}>
                Cost <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="cost"
                value={form.cost}
                onChange={handleChange}
                step="0.01"
                min="0"
                placeholder="0.00"
                className={FIELD_CLASS}
              />
            </div>
            <div>
              <label className={LABEL_CLASS}>
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="date"
                value={form.date}
                onChange={handleChange}
                className={FIELD_CLASS}
              />
            </div>
            <div>
              <label className={LABEL_CLASS}>Status</label>
              <select name="status" value={form.status} onChange={handleChange} className={FIELD_CLASS}>
                <option value="Pending">Pending</option>
                <option value="Completed">Completed</option>
              </select>
            </div>
            <div>
              <label className={LABEL_CLASS}>Performed By</label>
              <input
                type="text"
                name="performedBy"
                value={form.performedBy}
                onChange={handleChange}
                placeholder="Technician name..."
                className={FIELD_CLASS}
              />
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        open={Boolean(deleteTarget)}
        message="Are you sure you want to delete this maintenance record?"
        loading={loading}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default Maintenance;
